import React from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import LinkIcon from '@atlaskit/icon/core/link';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useCopyToClipboard } from '@confluence/copy-content-link/entry-points/useCopyToClipboard.ts';

import { showSnippetSuccessFlag, showSnippetErrorFlag } from '../../utils/snippetFlags.ts';

const messages = defineMessages({
	label: {
		id: 'snippets.snippetShareButton.label',
		defaultMessage: 'Copy share link',
		description: 'Label for the button that copies the snippet share link to clipboard',
	},
	successTitle: {
		id: 'snippets.snippetShareButton.successTitle',
		defaultMessage: 'Link copied',
		description: 'Success flag title when the snippet share link is copied to clipboard',
	},
	successDescription: {
		id: 'snippets.snippetShareButton.successDescription',
		defaultMessage: 'Template share link copied to clipboard',
		description: 'Success flag description when the snippet share link is copied to clipboard',
	},
	errorTitle: {
		id: 'snippets.snippetShareButton.errorTitle',
		defaultMessage: 'Failed to copy link',
		description: 'Error flag title when copying the snippet share link to clipboard fails',
	},
});

type SnippetShareButtonProps = {
	shareUrl: string;
} & WithFlagsProps;

const SnippetShareButtonInner: FC<SnippetShareButtonProps> = ({ shareUrl, flags }) => {
	const intl = useIntl();
	const { copyToClipboard } = useCopyToClipboard();

	const handleClick = async () => {
		const origin = window?.location?.origin ?? '';
		const fullUrl = `${origin}${shareUrl}`;
		try {
			await copyToClipboard(fullUrl);
			showSnippetSuccessFlag(
				flags,
				intl.formatMessage(messages.successTitle),
				intl.formatMessage(messages.successDescription),
			);
		} catch {
			showSnippetErrorFlag(flags, intl.formatMessage(messages.errorTitle));
		}
	};

	return (
		<Button appearance="subtle" iconBefore={LinkIcon} onClick={handleClick}>
			<FormattedMessage {...messages.label} />
		</Button>
	);
};

export const SnippetShareButton: React.ComponentType<
	Omit<
		{
			shareUrl: string;
		},
		'flags'
	>
> = withFlags(SnippetShareButtonInner);
